<template>
  <div class="column-wrapper">
    <div
      :id="'calendarColumn' + day"
      :ref="'refColumn'"
      class="day-wrapper"
      :class="{
        oddDay: day % 2 !== 0,
        today: currentTime.isBefore(endOfDay) && currentTime.isSameOrAfter(startOfDay),
      }"
      :style="{
        height: $parent.hour_height * 24 + 'px',
      }"
      @mousedown="mouseDown"
      @touchstart="mouseDown"
      @mouseup="columnClicked"
      @touchend="columnClicked"
    >
      <div class="hourLines">
        <div
          v-for="hour in 24"
          :key="hour + 'singleHourLine'"
          class="singleHourLine"
          :class="{
            boldLine: hour % 3 === 0,
            today: currentTime.isBefore(endOfDay) && currentTime.isSameOrAfter(startOfDay),
          }"
          :style="{
            top: $parent.hour_height * hour + 'px',
          }"
        />
      </div>
      <transition-group name="list" tag="span">
        <custom-draggable-resizable
          v-for="bar in bars"
          :x="Math.round((parentWidth - barWidth) / 2)"
          :key="bar.key"
          :w="barWidth"
          :y="bar.y"
          :active="bar.enabled"
          :h="bar.h"
          :grid="[0.5, gridSpacingComputed]"
          :handles="bar.handles"
          :on-resize-start="(handle, ev) => onResizeStart(handle, ev, bar)"
          :on-drag-start="(event) => onDragStart(bar, event)"
          :onDrag="(l, t, b, r, mouseX) => onDrag(l, t, b, r, mouseX, bar)"
          :onResize="(handle, x, y, width, height, mouseX) => onResize(handle, x, y, width, height, mouseX, bar)"
          @dragstop="(left, top) => onDragStop(left, top, bar)"
          @activated="onActivated(bar)"
          @deactivated="onDeactivated(bar)"
          @entryCreated="createBarOfEntry(entry)"
          :style="styleForBar(bar, currentTime)"
          class="hrs-bar"
          :entryId="bar.entryId"
        >
          <div
            v-if="isEditable"
            slot="tm"
            class="entries-handle"
            :style="{
              border: '1px ' + bar.color2 + ' solid',
              background: bar.color2,
            }"
          />
          <div
            v-if="isEditable"
            slot="bm"
            class="entries-handle"
            :style="{
              border: '1px ' + bar.color2 + ' solid',
              background: bar.color2,
            }"
          />

          <div v-if="!bar.type || bar.type == 'work'" class="entries-content">
            <div :ref="'refIco' + bar.key" :style="styleForIcon(bar)" class="ref-icon">
              <team-icon v-if="teamsDict.size > 1" class="icon" :team="bar.team" />
            </div>
            <div class="entries-name" :ref="'refName' + bar.key" :style="styleForEntryName(bar)">
              <div class="entries-name" :ref="'refName' + bar.key" :style="styleForEntryName(bar)">
                {{ bar.locationName.substr(0, 30) }}
              </div>
              <div v-if="dictionary[bar.entryId].comment" class="entries-comment" :ref="'refComment' + bar.key" :style="styleForCommentIcon(bar)">
                <svg width="13" height="13" xmlns="http://www.w3.org/2000/svg">
                  <path
                    :fill="bar.enabled ? bar.color3 : bar.color4"
                    fill-rule="nonzero"
                    d="M10.5 0c1.3254834 0 2.4100387 1.03153594 2.4946823 2.33562431L13 2.5v5c0 1.09875598-.7088238 2.03195729-1.6941435 2.3672761l-.166706.05027638L11.014 9.946l.0000381 2.6316643L6.786 10H2.5C1.22974508 10 .18075921 9.05263453.0210511 7.82591229l-.01573342-.1615366L0 7.5v-5C0 1.1745166 1.03153594.08996133 2.33562431.00531768L2.5 0h8zm0 1h-8c-.77969612 0-1.42044868.59488808-1.49313342 1.35553999L1 2.5v5c0 .77969612.59488808 1.42044868 1.35553999 1.49313342L2.5 9h4.56591797l2.94812013 1.7969971V9H10.5c.7796961 0 1.4204487-.59488808 1.4931334-1.35553999L12 7.5v-5c0-.77969612-.5948881-1.42044868-1.35554-1.49313342L10.5 1zm-4 4c.27614237 0 .5.22385763.5.5 0 .24545989-.17687516.44960837-.41012437.49194433L6.5 6h-3c-.27614237 0-.5-.22385763-.5-.5 0-.24545989.17687516-.44960837.41012437-.49194433L3.5 5h3zm2-2c.27614237 0 .5.22385763.5.5 0 .24545989-.17687516.44960837-.41012437.49194433L8.5 4h-5c-.27614237 0-.5-.22385763-.5-.5 0-.24545989.17687516-.44960837.41012437-.49194433L3.5 3h5z"
                  />
                </svg>
              </div>
            </div>

            <div class="entries-duration" :ref="'refDur' + bar.key">
              {{ formatBottomTime(bar) }}
            </div>
          </div>

          <div v-else class="entries-time-off-content" :ref="'timeOff' + bar.key" :style="styleForTimeOffContent(bar)">
            <div :ref="'refIco' + bar.key" :style="styleForIcon(bar)" class="ref-icon time-off-icon">
              <team-icon v-if="teamsDict.size > 1" class="icon" :team="bar.team" />
            </div>
            <div class="entries-name" :ref="'refName' + bar.key" :style="styleForEntryName(bar)">
              <div v-if="bar.type == 'holiday'">
                <img src="@/assets/_desktop/_calendars/holiday_14px.svg" />
              </div>
              <div v-else-if="bar.type == 'sick'">
                <img src="@/assets/_desktop/_calendars/sick_14px.svg" />
              </div>
              <div v-else>
                <img src="@/assets/_desktop/_calendars/smile_14px.svg" />
              </div>
              <div class="entries-comment" v-if="dictionary[bar.entryId].comment">
                <svg width="13" height="13" xmlns="http://www.w3.org/2000/svg">
                  <path
                    :fill="bar.enabled ? bar.color3 : bar.color4"
                    fill-rule="nonzero"
                    d="M10.5 0c1.3254834 0 2.4100387 1.03153594 2.4946823 2.33562431L13 2.5v5c0 1.09875598-.7088238 2.03195729-1.6941435 2.3672761l-.166706.05027638L11.014 9.946l.0000381 2.6316643L6.786 10H2.5C1.22974508 10 .18075921 9.05263453.0210511 7.82591229l-.01573342-.1615366L0 7.5v-5C0 1.1745166 1.03153594.08996133 2.33562431.00531768L2.5 0h8zm0 1h-8c-.77969612 0-1.42044868.59488808-1.49313342 1.35553999L1 2.5v5c0 .77969612.59488808 1.42044868 1.35553999 1.49313342L2.5 9h4.56591797l2.94812013 1.7969971V9H10.5c.7796961 0 1.4204487-.59488808 1.4931334-1.35553999L12 7.5v-5c0-.77969612-.5948881-1.42044868-1.35554-1.49313342L10.5 1zm-4 4c.27614237 0 .5.22385763.5.5 0 .24545989-.17687516.44960837-.41012437.49194433L6.5 6h-3c-.27614237 0-.5-.22385763-.5-.5 0-.24545989.17687516-.44960837.41012437-.49194433L3.5 5h3zm2-2c.27614237 0 .5.22385763.5.5 0 .24545989-.17687516.44960837-.41012437.49194433L8.5 4h-5c-.27614237 0-.5-.22385763-.5-.5 0-.24545989.17687516-.44960837.41012437-.49194433L3.5 3h5z"
                  />
                </svg>
              </div>
            </div>
          </div>
          <div
            v-if="!(!bar.type || bar.type == 'work')"
            class="entries-duration time-off-duration"
            :style="styleForTimeOffDuration(bar)"
            :ref="'refDur' + bar.key"
          >
            {{ formatBottomTime(bar) }}
          </div>
        </custom-draggable-resizable>
      </transition-group>
    </div>
  </div>
</template>

<script>
import TeamIcon from '@/components/Team/TeamIcon'
import CustomDraggableResizable from '@/components/Schedule/CustomDraggableResizable'
import moment from 'moment-timezone'
import { durationFormatted } from '../../../clavo-shared/functions/time'
import {
  collides,
  isSticked,
  getBestFittingPlaceEntry,
  resizingUpEntryBestFittingStart,
  resizingDownEntryBestFittingEnd,
} from '../../../clavo-shared/functions/collisions'
import Constants from '@/components/Constants'
import EventBus from '../../EventBus'

export function setBarTitle(bar, entry) {
  if (entry.type === 'work') {
    bar.locationName = entry.location.name
  }
  bar.type = entry.type
}
export function setBarColor(bar, entry) {
  if (entry.location && entry.type === 'work') {
    bar.color1 = 'white'
    bar.color2 = entry.location.color
    bar.color3 = entry.location.color
    bar.color4 = 'white'
  } else if (entry.type === 'holiday') {
    bar.color1 = 'white'
    bar.color2 = '#F6E4BB'
    bar.color3 = '#081014'
    bar.color4 = '#081014'
  } else if (entry.type === 'sick') {
    bar.color1 = 'white'
    bar.color2 = '#FAD3B2'
    bar.color3 = '#081014'
    bar.color4 = '#081014'
  } else {
    // days-off
    bar.color1 = 'white'
    bar.color2 = '#EBD2AE'
    bar.color3 = '#081014'
    bar.color4 = '#081014'
  }
}

export default {
  components: {
    CustomDraggableResizable,
    TeamIcon,
  },
  mixins: [Constants],
  props: {
    day: Number,
    startOfWeek: Date,
    endOfWeek: Date,
    startOfDay: Object,
    endOfDay: Object,
    userTimezone: String,
    entries: Array,
    dictionary: Array,
    hourHeight: {
      default: 28,
      type: Number,
    },
    teamsDict: Map,
    currentTime: Object,
    isMyPersonal: {
      default: false,
      type: Boolean,
    },
    submittingEntries: {
      default: false,
      type: Boolean,
    },
    fetchingEntries: {
      default: false,
      type: Boolean,
    },
  },

  data() {
    return {
      /** @type {number}
       * Default minimal time gap between two distinct entries on display in minutes */
      minTimeGapBetweenEntries: 15,
      /** @type {{top: number, bottom: number, day: number}}}
       * Coordinates of the currently selected bar ust before dragging */
      beginDraggingPosition: {},
      /** @type {Object} direction is one of {'bm', 'tm'}
       * Reaggable-resizable handler used to resize*/
      handle: { direction: undefined },
      /** @type {[Object]}
       * An array of displayed bars. Bars are in no specified order. Multiple bars may correspond
       * with one single entry */
      bars: [],
      keyInd: 0,
      barsByPosition: [], // todo what is this ??
      parentWidth: 100, // todo what is this ??
      barWidth: 50,

      activeEntryIndexes: [], // todo what is this ??
      /** @type {Object} (in this.entries)
       * Currently selected entry */
      activeEntry: {},
      /** @type {number}
       * A height of single hour label in day schedule in pixels */
      hourLabelHeight: this.$parent.hour_height,
      lastUpdatedEntry: undefined,
      currentColumnOfDraggedBar: undefined,
      currentColumnLeftOfDraggedBar: undefined,
      currentColumnWidthOfDraggedBar: undefined,
      mouseDownEvent: undefined,
      mouseDownScrollX: undefined,
      isThereDstThisDay: false,
    }
  },
  computed: {
    personal() {
      return this.$parent
    },
    columns() {
      return this.$parent.$refs['refColumn']
    },
    gridSpacingComputed() {
      return this.hourHeight / (60 / this.minTimeGapBetweenEntries)
    },
    isEditable() {
      return this.isMyPersonal && !this.submittingEntries && !this.fetchingEntries
    },
  },

  watch: {
    startOfDay: function () {
      this.updateIsThereDST()
    },
  },
  //  async created() {
  //   //   // let ind = 0
  //   //   //  // console.log("CREATED",this.startOfDay)
  //   let rrr = moment(this.currentTime).isBefore(this.endOfDay) && moment(this.currentTime).isSameOrAfter(this.startOfDay)
  //   rrr = rrr
  //  },

  created() {
    this.updateIsThereDST()
  },

  async mounted() {
    this.updateWidth()
    window.addEventListener('resize', this.changeSizeReactive)
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.changeSizeReactive)
  },

  methods: {
    updateIsThereDST() {
      let isStartOfDayInDST = moment(this.startOfDay).tz(this.userTimezone).isDST()
      let isEndOfDayInDST = moment(this.endOfDay).tz(this.userTimezone).isDST()
      this.isThereDstThisDay = isStartOfDayInDST != isEndOfDayInDST
    },
    findHolidayType(bar) {
      return this.daysOffTypes().find((x) => x.value === bar.type)
    },
    styleForIcon(bar) {
      let key1 = 'refIco' + bar.key
      let key2 = 'refDur' + bar.key
      let element1 = this.$refs[key1]
      let element2 = this.$refs[key2]
      let noOverlap = true

      if (element1 && element1.length > 0 && element2 && element2.length > 0) {
        let rect1 = element1[0].getBoundingClientRect()
        let rect2 = element2[0].getBoundingClientRect()
        noOverlap = rect1.right < rect2.left || rect1.left > rect2.right || rect1.bottom < rect2.top || rect1.top > rect2.bottom
      }
      return { opacity: noOverlap ? 1 : 0.2 }
    },
    styleForEntryName(bar) {
      let key1 = 'refName' + bar.key
      let key2 = 'refDur' + bar.key
      let element1 = this.$refs[key1]
      let element2 = this.$refs[key2]
      let noOverlap = true

      if (element1 && element1.length > 0 && element2 && element2.length > 0) {
        let rect1 = element1[0].getBoundingClientRect()
        let rect2 = element2[0].getBoundingClientRect()
        noOverlap = rect1.right < rect2.left || rect1.left > rect2.right || rect1.bottom < rect2.top || rect1.top > rect2.bottom
      }
      return { opacity: noOverlap ? 1 : 0.2 }
    },
    styleForCommentIcon(bar) {
      let key1 = 'refComment' + bar.key
      let key2 = 'refDur' + bar.key
      let element1 = this.$refs[key1]
      let element2 = this.$refs[key2]
      let noOverlap = true

      if (element1 && element1.length > 0 && element2 && element2.length > 0) {
        let rect1 = element1[0].getBoundingClientRect()
        let rect2 = element2[0].getBoundingClientRect()
        noOverlap = rect1.bottom <= rect2.top
      }
      return { opacity: noOverlap ? 1 : 0.2 }
    },
    styleForTimeOffContent(bar) {
      let key = 'timeOff' + bar.key
      let element = this.$refs[key]
      let topValue = 47
      if (element && element.length > 0) {
        let entryBody = element[0].parentElement.getBoundingClientRect()
        let columnWrapperRect = document.getElementsByClassName('column-wrapper')[0].getBoundingClientRect()

        topValue = Math.round(columnWrapperRect.top - entryBody.top + 47)
      }
      return { top: topValue + 'px' }
    },
    styleForTimeOffDuration(bar) {
      let key = 'timeOff' + bar.key
      let element = this.$refs[key]
      let bottomValue = 6
      if (element && element.length > 0) {
        let entryBody = element[0].parentElement.getBoundingClientRect()
        let columnWrapperRect = document.getElementsByClassName('column-wrapper')[0].getBoundingClientRect()

        bottomValue = Math.round(entryBody.top - columnWrapperRect.top + 6)
      }
      return { bottom: bottomValue + 'px' }
    },
    updateWidth() {
      let element = document.getElementById('calendarColumn' + this.day)
      if (element) {
        this.parentWidth = element.parentElement.clientWidth
        this.barWidth = this.parentWidth <= 100 ? Math.min(this.parentWidth * 0.8, this.parentWidth / 2 / (this.parentWidth / 100)) : this.parentWidth / 2
      }
    },
    formatBottomTime(bar) {
      let entry = this.dictionary[bar.entryId]
      return durationFormatted(entry.start, entry.end, this.parentWidth, entry.type)
    },
    styleForBar(bar, timeMoment) {
      let time = timeMoment.toDate()
      // console.log('styleFOR BAR', bar.day)

      let entry = this.dictionary[bar.entryId]
      let start = entry.start
      let end = entry.end
      let barStartOfDay = bar.startOfDay
      let barEndOfDay = bar.endOfDay

      if (start < barStartOfDay) {
        start = barStartOfDay
      }
      if (end > barEndOfDay) {
        end = barEndOfDay
      }

      let diffStart = time - start
      let diffEnd = end - time
      let len = end - start
      var style
      // if(bar.dayOff){
      //   let oldP = (diffStart * 100.0) / len
      //   style = {
      //     background: 'linear-gradient(' + (bar.color2 + 80) + ' ' + oldP + '%, ' + (bar.color2 + 80) + ' ' + oldP + '% 100%)',
      //     border: '0px ' + (bar.enabled ? bar.color2 : time > entry.start ? bar.color2 + 80 : bar.color2) + ' solid',
      //     color: bar.color4,
      //     visibility: bar.h > 0 ? 'visible' : 'hidden',
      //     width: '100%',
      //     cursor: 'unset',
      //   }
      // }
      // else
      if (bar.selectWeek) {
        // in the middle of current time
        let oldP = (diffStart * 100.0) / len
        style = {
          background: bar.color2 + 80,
          border: bar.enabled ? '1px ' : '0px ' + (bar.enabled ? bar.color2 : time > entry.start ? bar.color2 + 80 : bar.color2) + ' solid',
          color: bar.enabled ? bar.color3 : bar.color4,
          visibility: bar.h > 0 ? 'visible' : 'hidden',
          cursor: 'unset',
        }
      } else if (diffStart > 0 && diffEnd > 0 && !bar.enabled) {
        // in the middle of current time
        let oldP = (diffStart * 100.0) / len
        style = {
          background: 'linear-gradient(' + (bar.color2 + 80) + ' ' + oldP + '%, ' + bar.color2 + ' ' + oldP + '% 100%)',
          border: bar.enabled ? '1px ' : '0px ' + (bar.enabled ? bar.color2 : time > entry.start ? bar.color2 + 80 : bar.color2) + ' solid',
          color: bar.enabled ? bar.color3 : bar.color4,
          visibility: bar.h > 0 ? 'visible' : 'hidden',
        }
      } else {
        style = {
          backgroundColor: bar.enabled ? bar.color1 : time > start ? bar.color2 + 80 : bar.color2,
          border: (time > start && !bar.enabled ? '0px ' : '1px ') + (bar.enabled ? bar.color2 : time > start ? bar.color2 + 80 : bar.color2) + ' solid',
          color: bar.enabled ? bar.color3 : bar.color4,
          visibility: bar.h > 0 ? 'visible' : 'hidden',
        }
      }
      if (!bar.enabled) {
        style['border-bottom'] = '1px rgb(255,255,255,.3) solid'
      }

      style.justifyContent = 'space-between'

      return style
    },
    recreateAllBars() {
      this.updateWidth()
      if (this.bars.length > 0) {
        this.bars = []
      }
      for (let i = 0; i < this.entries.length; i++) {
        let entry = this.entries[i]
        this.createBarOfEntry(entry)
      }
    },

    columnClicked(e) {
      this.$emit('columnClicked', e, this)
    },
    mouseDown(e) {
      this.mouseDownScrollX = window.top.scrollX
      this.mouseDownEvent = e
    },
    setEntryActive(entry) {
      let bar = this.bars.filter((b) => b.entryId === entry.id)
      if (bar.length > 0) {
        bar[0].enabled = true
      }
    },
    createBarOfEntry(entry) {
      if (
        !(entry.deletedAt || entry.isDeleted) &&
        moment(entry.start).isBefore(this.endOfDay) &&
        moment(entry.end).isAfter(this.startOfDay) &&
        entry.location
      ) {
        let key = 'bar' + this.day + '-' + this.keyInd++
        let bar = {
          y: this.startPositionOfBar(entry.start, entry.timezone),
          h: this.barHeightMinutes(entry.start, entry.end, entry.timezone),
          endPosition: this.startPositionOfBar(entry.start, entry.timezone) + this.barHeightMinutes(entry.start, entry.end, entry.timezone),
          color1: 'white',
          color2: entry.location.color,
          color3: entry.location.color,
          color4: 'white',
          entryId: entry.id,
          locationName: entry.location.name,
          team: this.teamsDict.get(entry.teamId),
          handles: entry.type === 'work' ? ['tm', 'bm'] : [],
          type: entry.type,
          dayOff: !!this.findHolidayType(entry),
          enabled: false,
          dragged: false,
          startOfDay: this.startOfDay,
          endOfDay: this.endOfDay,
          day: this.day,
          key: key,
          selectWeek: entry.selectWeek,
        }

        setBarColor(bar, entry)
        setBarTitle(bar, entry)

        this.bars.push(bar)
      }
    },
    getStartAndHeightFromModel(updatedEntry, colDiff) {
      //let endOfDay = moment(this.startOfDay).add(1, 'days')
      var lM, tM, rM, bM, hM
      let start = moment(updatedEntry.start)
      let end = moment(updatedEntry.end)
      var modifyH = false

      if (colDiff != 0 || start.isAfter(this.endOfDay) || end.isBefore(this.startOfDay)) {
        return { lM, tM: 0, rM, bM: 0, hM }
      }

      if (start.isBefore(this.startOfDay)) {
        start = this.startOfDay
        tM = 0
      } else if (start.isSameOrBefore(this.endOfDay)) {
        tM = this.startPositionOfBar(start, updatedEntry.timezone)
      } else {
        tM = this.hourHeight * 24
      }
      if (end.isAfter(this.endOfDay)) {
        end = this.endOfDay
      }

      hM = this.barHeightMinutes(start, end, updatedEntry.timezone)
      // tM = this.startPositionOfBar(start)
      bM = -hM - tM

      return { lM, tM, rM, bM, hM }
    },
    getCurrentColumnToDrag(l, r, mouseX, type) {
      if (this.parentWidth > 60) {
        return this.getCurrentColumnToDragDesktop(l, r, mouseX, type)
      } else {
        return this.getCurrentColumnToDragMobile(l, r, mouseX, type)
      }
    },
    getCurrentColumnToDragDesktop(l, r, mouseX, type) {
      // console.log("xMouseDiff",mouseX)

      let elCurrent = document.getElementById('dayColumn' + this.day)
      let firstOffset = elCurrent.offsetLeft

      let scrollX = window.top.scrollX

      for (let i = 0; i < this.columns.length; i++) {
        var c = this.columns[i]
        let el = document.getElementById('dayColumn' + i)

        if (i == 0) {
          var rectL = el.getBoundingClientRect().left + scrollX
        }

        let oL = el.offsetLeft
        let oW = el.offsetWidth
        let oR = oL + oW

        let cL = oL - firstOffset
        if (type) {
          if (i == this.day) {
            return { columnLeft: cL, columnWidth: oW, column: c }
          }
        } else {
          if ((mouseX - rectL >= oL || i == 0) && (mouseX - rectL <= oR || i == this.columns.length - 1)) {
            // console.log('column to drag', i)
            return { columnLeft: cL, columnWidth: oW, column: c }
          }
        }
      }
      return null // not possible
    },
    getCurrentColumnToDragMobile(l, r, mouseX, type) {
      // console.log("xMouseDiff",mouseX)
      let elCurrent = document.getElementById('dayColumn' + this.day)

      let firstOffset = elCurrent.offsetLeft
      for (let i = 0; i < this.columns.length; i++) {
        var c = this.columns[i]
        let el = document.getElementById('dayColumn' + i)

        let oL = el.offsetLeft - firstOffset
        let oW = el.offsetWidth
        if (type) {
          if (i == this.day) {
            return { columnLeft: oL, columnWidth: oW, column: c }
          }
        } else {
          if ((l >= oL || i == 0) && (r <= oL + oW || i == this.columns.length - 1)) {
            return { columnLeft: oL, columnWidth: oW, column: c }
          }
        }
      }
      return null // not possible
    },
    getXForBarInColumn(columnLeft, columnWidth, barWidth) {
      return columnLeft + (columnWidth - barWidth) / 2.0
    },
    getVerticalMinutesDiff(newY, colDiff, isBottomCheck) {
      if (isBottomCheck) {
        var diffY = newY - this.beginDraggingPosition.bottom
      } else {
        var diffY = newY - this.beginDraggingPosition.top
      }
      var minutesDiff = Math.ceil((diffY / this.hourLabelHeight) * 60) + colDiff * 60 * 24

      if (minutesDiff > 0 && minutesDiff < this.minTimeGapBetweenEntries) {
        minutesDiff = this.minTimeGapBetweenEntries
      } else if (minutesDiff < 0 && minutesDiff > -1 * this.minTimeGapBetweenEntries) {
        minutesDiff = this.minTimeGapBetweenEntries * -1
      }
      minutesDiff -= minutesDiff % this.minTimeGapBetweenEntries
      return minutesDiff
    },

    getUpdatedEntry(minutesDiffTop, minutesDiffBottom) {
      let updatedEntry = { ...this.lastUpdatedEntry }
      if (minutesDiffTop) {
        updatedEntry.start = moment(this.lastUpdatedEntry.start).add(minutesDiffTop, 'minutes').toDate()
      }
      if (minutesDiffBottom) {
        updatedEntry.end = moment(this.lastUpdatedEntry.end).add(minutesDiffBottom, 'minutes').toDate()
      }
      return updatedEntry
    },

    getUpdatedTimeOffEntry(dayDifference) {
      let updatedEntry = { ...this.lastUpdatedEntry }
      updatedEntry.start = moment(this.lastUpdatedEntry.start).tz(this.lastUpdatedEntry.timezone).add(dayDifference, 'days').toDate()
      updatedEntry.end = moment(this.lastUpdatedEntry.end).tz(this.lastUpdatedEntry.timezone).add(dayDifference, 'days').toDate()
      return updatedEntry
    },

    onDrag(l, t, r, b, mouseX, bar) {
      if (!this.isEditable) return

      const MINUTES_THRESHHOLD = 30
      let barWidth = -l - r

      var getC = this.getCurrentColumnToDrag(l, -r, mouseX)

      if (!getC) {
        /* if we are between 2 days we use old column */
        if (this.currentColumnOfDraggedBar) {
          columnLeft = this.currentColumnLeftOfDraggedBar
          columnWidth = this.currentColumnWidthOfDraggedBar
          column = this.currentColumnOfDraggedBar
        } else {
          return false
        }
      } else {
        var { columnLeft, columnWidth, column } = getC
        this.currentColumnOfDraggedBar = column
        this.currentColumnWidthOfDraggedBar = columnWidth
        this.currentColumnLeftOfDraggedBar = columnLeft
      }
      //console.log("currentColumnLeftOfDraggedBar",this.currentColumnLeftOfDraggedBar)

      l = this.getXForBarInColumn(columnLeft, columnWidth, barWidth)
      r = -l - barWidth

      if (this.activeEntry.type === 'work') {
        if (t > this.hourHeight * 24) {
          b = b - t
          t = this.hourHeight * 24
        }
        if (b > 0) {
          t = t + b
          b = 0
        }
      } else {
        t = this.beginDraggingPosition.top
        b = this.beginDraggingPosition.bottom
      }
      const entriesArray = this.entries

      const colDiff = column.day - this.day
      const verticalMinutesDiff = this.getVerticalMinutesDiff(t, colDiff, false)
      if (verticalMinutesDiff != 0) {
        bar.dragged = true
      }
      // console.log("veriticaMinDiff",verticalMinutesDiff, t))
      const sticked = isSticked(entriesArray, this.activeEntry) /* check if last position sticked */

      let updatedEntry
      if (this.lastUpdatedEntry.type === 'work') {
        updatedEntry = this.getUpdatedEntry(verticalMinutesDiff, verticalMinutesDiff)
      } else {
        updatedEntry = this.getUpdatedTimeOffEntry(Math.round(verticalMinutesDiff / (60 * 24)))
      }

      var h

      if (collides(entriesArray, updatedEntry)) {
        if (this.activeEntry.type === 'work') {
          let candidate = getBestFittingPlaceEntry(entriesArray, updatedEntry, sticked, MINUTES_THRESHHOLD)
          if (!candidate) {
            return false
          } else {
            // can't drag out totally from calendar
            if (candidate.end <= this.startOfWeek || candidate.start >= this.endOfWeek) {
              return false
            }
            updatedEntry.start = candidate.start
            updatedEntry.end = candidate.end
          }
        } else {
          return false
        }
      }

      if (verticalMinutesDiff != 0) {
        if (!bar.draggedEffectively) {
          bar.draggedEffectively = true

          // for (b of this.bars){
          //   // console.log("BAR y ", b.y)
          //   // console.log("BAR y ", b.y)
          //   // console.log("BAR h ", b.h)
          // }

          this.$emit('closePopup', 'drag')
        }
      }

      this.activeEntry.start = updatedEntry.start
      this.activeEntry.end = updatedEntry.end

      // console.log('updatedEntry start', updatedEntry.start)
      let { lM, tM, rM, bM, hM } = this.getStartAndHeightFromModel(updatedEntry, colDiff)

      l = lM != null ? lM : l
      t = tM != null ? tM : t
      r = rM != null ? rM : r
      b = bM != null ? bM : b
      h = hM != null ? hM : h

      if (h === 0) h = undefined // rare case, h has a validator that checks if h > 0
      if (typeof h == 'number') bar.h = Math.min(h, 24 * this.hourHeight - t)
      else bar.h = h
      bar.y = t

      this.$emit('entryBarUpdate', updatedEntry, bar, 'drag', { draggedToColumn: column.day })
      // console.log({ l, t, r, b, h })
      return { l, t, r, b, h }
    },
    onDragStart(bar, event) {
      // console.log('onDragStart')
      if (bar.selectWeek) return false
      this.inializeBarChangesPhase(bar)
      bar.dragged = false
    },

    onResize(handle, l, t, w, h, mouseX, bar) {
      if (!this.isEditable) return

      const MINUTES_THRESHHOLD = 30
      const MIN_ENTRY_MINUTES = 15
      let barWidth = w
      let r = -l - w
      let b = -t - h

      if (handle == 'bm') {
        var type = 'resize-bottom'
      } else {
        var type = 'resize-top'
      }

      var getC = this.getCurrentColumnToDrag(l, -r, mouseX, type)

      // console.log("this.getCurrentColumnToDrag(l, -r, mouseX)", l, -r, mouseX)

      if (!getC) {
        /* if we are between 2 days we use old column */
        if (this.currentColumnOfDraggedBar) {
          columnLeft = this.currentColumnLeftOfDraggedBar
          columnWidth = this.currentColumnWidthOfDraggedBar
          column = this.currentColumnOfDraggedBar
        } else {
          return false
        }
      } else {
        var { columnLeft, columnWidth, column } = getC
        this.currentColumnOfDraggedBar = column
        this.currentColumnWidthOfDraggedBar = columnWidth
        this.currentColumnLeftOfDraggedBar = columnLeft
      }

      l = this.getXForBarInColumn(columnLeft, columnWidth, barWidth)
      r = -l - barWidth

      if (t > this.hourHeight * 24) {
        b = b - t
        t = this.hourHeight * 24
      }
      if (b > 0) {
        t = t + b
        b = 0
      }
      const entriesArray = this.entries

      const colDiff = column.day - this.day
      if (handle == 'bm') {
        var verticalMinutesDiff = this.getVerticalMinutesDiff(-b, colDiff, true)
      } else {
        var verticalMinutesDiff = this.getVerticalMinutesDiff(t, colDiff, false)
      }

      // console.log('vertical min diff', verticalMinutesDiff, -b, colDiff)

      if (verticalMinutesDiff != 0) {
        bar.dragged = true
      }

      if (handle == 'bm') {
        var updatedEntry = this.getUpdatedEntry(0, verticalMinutesDiff)
        if (updatedEntry.start >= updatedEntry.end) {
          updatedEntry.end = new Date(updatedEntry.start.getTime() + MIN_ENTRY_MINUTES * 60 * 1000)
        }
      } else {
        var updatedEntry = this.getUpdatedEntry(verticalMinutesDiff, 0)
        if (updatedEntry.start >= updatedEntry.end) {
          updatedEntry.start = new Date(updatedEntry.end.getTime() - MIN_ENTRY_MINUTES * 60 * 1000)
        }
      }

      var h

      if (collides(entriesArray, updatedEntry)) {
        if (handle == 'bm') {
          var candidate = resizingDownEntryBestFittingEnd(entriesArray, updatedEntry, MINUTES_THRESHHOLD)
        } else {
          var candidate = resizingUpEntryBestFittingStart(entriesArray, updatedEntry, MINUTES_THRESHHOLD)
        }
        if (!candidate) {
          return false
        } else {
          // can't drag out totally from calendar
          if (candidate.end <= this.startOfWeek || candidate.start >= this.endOfWeek) {
            return false
          }
          updatedEntry.start = candidate.start
          updatedEntry.end = candidate.end
        }
      }

      if (verticalMinutesDiff != 0) {
        if (!bar.draggedEffectively) {
          bar.draggedEffectively = true
          this.$emit('closePopup', 'drag')
        }
      }

      this.activeEntry.start = updatedEntry.start
      this.activeEntry.end = updatedEntry.end
      this.$emit('entryBarUpdate', updatedEntry, bar, type)

      // console.log('updatedEntry start', updatedEntry.start)
      let { lM, tM, rM, bM, hM } = this.getStartAndHeightFromModel(updatedEntry, colDiff)

      l = lM != null ? lM : l
      t = tM != null ? tM : t
      r = rM != null ? rM : r
      b = bM != null ? bM : b
      h = hM != null ? hM : h

      if (h === 0) h = undefined // rare case, h has a validator that checks if h > 0
      if (typeof h == 'number') bar.h = Math.min(h, 24 * this.hourHeight - t)
      else bar.h = h
      bar.y = t
      // console.log({ l, t, r, b, h })
      return { l, t, r, b, h }
    },
    inializeBarChangesPhase(bar) {
      // console.log('--------------inializeBarChangesPhase')
      this.activeEntry = this.dictionary[bar.entryId]
      this.lastUpdatedEntry = { ...this.activeEntry }
      this.$emit('activatedBar', bar, this.activeEntry, this.lastUpdatedEntry)
      this.beginDraggingPosition = {
        top: bar.y,
        bottom: bar.y + bar.h,
        day: this.day,
      }
    },
    onResizeStart(handle, ev, bar) {
      // console.log('onResizeStart')
      this.inializeBarChangesPhase(bar)
      this.$emit('closePopup', 'resize')
    },

    enableBar(bar) {
      for (let i = 0; i < this.columns.length; i++) {
        for (let j = 0; j < this.columns[i].bars.length; j++) {
          if (this.columns[i].bars[j].entryId === bar.entryId) {
            let handles = []
            let entry = this.dictionary[this.columns[i].bars[j].entryId]
            if (entry.type === 'work') {
              if (moment(entry.start).isSameOrAfter(this.columns[i].startOfDay)) handles.push('tm')
              if (moment(entry.end).isSameOrBefore(this.columns[i].endOfDay)) handles.push('bm')
            }
            this.columns[i].bars[j].handles = handles
            this.columns[i].bars[j].enabled = true
          }
        }
      }
    },
    disableBar(barToDisable) {
      // console.log("....... disable bar......")
      for (let column of this.columns) {
        for (let bar of column.bars) {
          if (bar.entryId === barToDisable.entryId) {
            let handles = []
            bar.handles = handles
            bar.enabled = false
          }
        }
      }
    },

    startPositionOfBar(start, timezone) {
      // handle daylight saving time
      let daylightSavingOffset = 0
      if (this.isThereDstThisDay) {
        let isStartOfDayInDST = moment(this.startOfDay).tz(this.userTimezone).isDST()
        let isStartOfEntryInDST = moment(start).tz(this.userTimezone).isDST()
        if (isStartOfDayInDST && !isStartOfEntryInDST) {
          daylightSavingOffset = -1
        } else if (!isStartOfDayInDST && isStartOfEntryInDST) {
          daylightSavingOffset = 1
        }
      }

      let position =
        Math.floor(moment(start).diff(this.startOfDay, 'hours')) * this.hourHeight +
        Math.floor(((moment(start).diff(this.startOfDay, 'minutes') % 60) / 60) * this.hourHeight) +
        this.hourHeight * daylightSavingOffset

      return position > 0 ? position : 0
    },

    barHeightMinutes(start, end, timezone) {
      if (moment(start).isSameOrBefore(this.startOfDay) && moment(end).isSameOrAfter(this.endOfDay)) return 24 * this.hourHeight

      let startOfBar = moment(start),
        endOfBar = moment(end)
      if (startOfBar.isBefore(this.startOfDay)) {
        startOfBar = this.startOfDay
      }
      if (endOfBar.isAfter(this.endOfDay)) {
        endOfBar = this.endOfDay
      }

      // handle daylight saving time
      let daylightSavingOffset = 0
      if (this.isThereDstThisDay) {
        let isStartInDST = moment(startOfBar).tz(timezone).isDST()
        let isEndInDST = moment(endOfBar).tz(timezone).isDST()
        if (isStartInDST && !isEndInDST) {
          daylightSavingOffset = -1
        } else if (!isStartInDST && isEndInDST) {
          daylightSavingOffset = 1
        }
      }

      return (moment.duration(endOfBar.diff(startOfBar)).asMinutes() / 60 + daylightSavingOffset) * this.hourHeight
    },
    onDragStop(left, top, bar) {
      if (bar.draggedEffectively) {
        this.disableBar(bar)
      }
    },
    onActivated(bar) {
      EventBus.$emit('isBarActivated', true)
      bar.draggedEffectively = false
      // this.inializeBarChangesPhase(bar)
      this.enableBar(bar)
    },

    onDeactivated(bar) {
      EventBus.$emit('isBarActivated', false)
      this.disableBar(bar)
      this.$emit('closePopup', 'deactivated')
    },

    changeSizeReactive() {
      this.updateWidth()
    },
  },
}
</script>
<style scoped lang="scss">
.column-wrapper {
  padding-bottom: 24px;
  flex: 1 0px;
  .day-wrapper {
    position: relative;

    &.oddDay {
      background: rgba($soap, 0.2);
    }

    &.today {
      background: rgba($turquoise, 0.2);
    }

    .hourLines {
      position: relative;

      .singleHourLine {
        position: absolute;
        width: 100%;
        background: $athens-gray;
        height: 1px;

        &.boldLine {
          height: 2px;
        }
        &.today {
          background: $athens-gray-on-gray;
        }
      }
    }

    .hrs-bar {
      font-family: $semibold;
      font-size: 12px;
      color: white;
      letter-spacing: 0.2px;
      line-height: 14px;
      padding: 2px 4px 4px 4px;
      cursor: move;
      display: flex;
      flex-direction: column;
      transition: color 0.28s ease-out;
      transition: background-color 0.28s ease-out;

      &.active {
        box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.15);
      }
    }

    .list-enter-active,
    .list-leave-active {
      transition: opacity 0.3s;
    }
    .list-enter,
    .list-leave-to {
      opacity: 0;
    }
  }
}

.entries-handle {
  // width: calc(100% + 2px); /** (left for optional usage later) */
  width: 30px;
  height: 7px;
  margin: 0 auto;
  position: relative;
  cursor: ns-resize;

  &:after {
    content: '';
    display: block;
    background-color: white;
    width: 20px;
    left: 50%;
    transform: translateX(-50%);
    height: 1px;
    position: absolute;
    top: 2px;
  }
}

.entries-name {
  font-family: $semibold;
  hyphens: auto;
  padding-top: 0px;
}
.entries-duration {
  position: absolute;
  bottom: 0px;
  font-family: $semibold;
}

.entries-content {
  overflow: hidden;
  height: 100%;
  position: relative;
  .icon {
    margin: 3px 0;
    transform: scale(0.9);
    transform-origin: 0 0;
  }
}

.entries-time-off-content {
  position: sticky;
  display: grid;
  gap: 5px;
  overflow: hidden;
  margin-bottom: 14px;
}
.time-off-icon {
  margin: 3px 0;
}
.time-off-duration {
  position: sticky;
}

.entries-comment {
  margin-top: 6px;
}
</style>
